import { useAuthStore } from '@/stores/authStore'
import type { Branch, HttpMethod, ApiError } from '@/types/api'

/**
 * API Client for Alpha-Trader.
 *
 * This client handles authentication headers and error responses.
 * It mirrors the pattern used in v1's consolesend function but uses modern fetch API.
 */

const BASE_DOMAIN = 'alpha-trader.com'

/**
 * Get the full API URL for a given path.
 */
function getApiUrl(path: string, branch: Branch): string {
  // In development, use the Vite proxy
  if (import.meta.env.DEV) {
    return path.startsWith('/') ? path : `/${path}`
  }

  // In production, use the full URL with branch subdomain
  return `https://${branch}.${BASE_DOMAIN}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * Custom error class for API errors.
 */
export class ApiRequestError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: number,
    public messagePrototype?: ApiError['messagePrototype']
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }
}

/**
 * Make an authenticated API request.
 */
export async function apiRequest<T>(
  path: string,
  options: {
    method?: HttpMethod
    body?: unknown
    params?: Record<string, string | number | boolean | undefined>
  } = {}
): Promise<T> {
  const { method = 'GET', body, params } = options
  const { branch, getToken } = useAuthStore.getState()
  const token = getToken()

  // Build URL with query parameters
  let url = getApiUrl(path, branch)
  if (params) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value))
      }
    })
    const queryString = searchParams.toString()
    if (queryString) {
      url += (url.includes('?') ? '&' : '?') + queryString
    }
  }

  // Build request headers
  const headers: HeadersInit = {
    Accept: 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  if (body && typeof body === 'object') {
    headers['Content-Type'] = 'application/json'
  }

  // Make the request
  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })

  // Handle non-OK responses
  if (!response.ok) {
    let errorData: ApiError | null = null
    try {
      errorData = await response.json()
    } catch {
      // Response wasn't JSON
    }

    throw new ApiRequestError(
      errorData?.messagePrototype?.message || `HTTP ${response.status}`,
      response.status,
      errorData?.code,
      errorData?.messagePrototype
    )
  }

  // Handle empty responses
  const contentType = response.headers.get('content-type')
  if (!contentType || !contentType.includes('application/json')) {
    return undefined as T
  }

  return response.json()
}

/**
 * Convenience methods for common HTTP verbs.
 */
export const api = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) =>
    apiRequest<T>(path, { method: 'GET', params }),

  post: <T>(
    path: string,
    body?: unknown,
    params?: Record<string, string | number | boolean | undefined>
  ) => apiRequest<T>(path, { method: 'POST', body, params }),

  put: <T>(
    path: string,
    body?: unknown,
    params?: Record<string, string | number | boolean | undefined>
  ) => apiRequest<T>(path, { method: 'PUT', body, params }),

  patch: <T>(
    path: string,
    body?: unknown,
    params?: Record<string, string | number | boolean | undefined>
  ) => apiRequest<T>(path, { method: 'PATCH', body, params }),

  delete: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) =>
    apiRequest<T>(path, { method: 'DELETE', params }),
}
